
/* ============================== src/hooks/useTaskTimeLog.js ============================== */
// src/hooks/useTaskTimeLog.js
/**
 * Учёт времени в карточке задачи: состояние формы (logHours/logNote/
 * logDate/editingLogId), saveLog через useCommitTimeLog.commit (единая
 * логика 409-handling и похвалы), deleteLog через deleteLogMutation.
 * commit сохраняет фронт-валидацию (статус/часы/дата) и обрабатывает
 * 409 needsConfirmation (HOURLY_PLAN_EXCEEDED / OVERTIME_LIMIT_EXCEEDED /
 * OVERTIME_YEAR_EXCEEDED / NON_WORKING_DAY / DAILY_NORM_EXCEEDED).
 */
import { useState, useCallback } from "react";
import { TODAY } from "../utils/date";
import { useDeleteTaskLogMutation } from "../api/queries/tasks";
import { useCommitTimeLog } from "./useCommitTimeLog";

export function useTaskTimeLog({ existing, ur, toast }) {
  const [logHours, setLogHours] = useState("");
  const [logNote, setLogNote] = useState("");
  const [logDate, setLogDate] = useState(TODAY);
  const [editingLogId, setEditingLogId] = useState(null);

  const deleteLogMutation = useDeleteTaskLogMutation();
  const { commit, praise, closePraise } = useCommitTimeLog({ currentUser: ur });

  const startEdit = useCallback((log) => {
    setEditingLogId(log.id);
    setLogDate(log.date);
    setLogHours(String(log.hours));
    setLogNote(log.note || "");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingLogId(null);
    setLogHours("");
    setLogNote("");
    setLogDate(TODAY);
  }, []);

  const deleteLog = useCallback(
    async (logId) => {
      if (!existing) return;
      try {
        await deleteLogMutation.mutateAsync({ id: existing.id, logId });
        toast("Запись часов удалена", "success");
        if (editingLogId === logId) cancelEdit();
      } catch (err) {
        toast(err.message || "Ошибка удаления записи", "error");
      }
    },
    [existing, deleteLogMutation, toast, editingLogId, cancelEdit],
  );

  const saveLog = useCallback(async () => {
    if (!existing) return;

    if (!logHours) {
      toast("Введите корректное количество часов", "error");
      return;
    }
    const hours = parseFloat(logHours);
    if (isNaN(hours) || hours <= 0) {
      toast("Введите корректное количество часов", "error");
      return;
    }

    let result;
    try {
      result = await commit({
        task: existing,
        entry: {
          hours,
          date: logDate,
          note: logNote,
          editingLogId,
        },
      });
    } catch (err) {
      toast(err.message || "Ошибка сохранения", "error");
      return;
    }

    if (!result.ok) {
      if (result.reason === "validation") {
        toast(result.error, "error");
      }
      return;
    }

    toast(editingLogId ? "Запись обновлена" : "Часы учтены", "success");
    setLogHours("");
    setLogNote("");
    setLogDate(TODAY);
    setEditingLogId(null);
  }, [existing, logHours, logDate, logNote, editingLogId, commit, toast]);

  return {
    logHours,
    setLogHours,
    logNote,
    setLogNote,
    logDate,
    setLogDate,
    editingLogId,
    saveLog,
    startEdit,
    cancelEdit,
    deleteLog,
    praise,
    closePraise,
  };
}
